import React, { useState, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Sparkles, FileText, ChevronRight, Loader2, Zap, AlertCircle } from 'lucide-react';
import { createContentTask, generateOutline, generateOutlineStream, submitTaskGeneration, updateTaskOutline } from '../../services/contentService';
import { getSkills } from '../../services/skillService';
import { parseOutlinePages } from '../../pages/ContentHistory/historyUtils';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

/* ══════════════════════════════════════════════════════════
   ContentWizardModal — 内容创作向导（两步）
   步骤 1: 配置（主题 + 页数）
   步骤 2: 大纲编辑（AI 生成 + 逐页可编辑）
   提交后创建异步任务 → 跳转历史页
   ══════════════════════════════════════════════════════════ */

const PAGE_OPTIONS = [1, 3, 5, 7, 9];
const SUBMIT_IMAGE_COST = 6;
const MAX_IMAGE_COUNT = 9;
const TOPIC_MAX_LENGTH = 500;
const SKILL_OPTIONS = [
    { value: 'xiaohongshu_default', label: '通用笔记', hint: '中性信息流' },
    { value: 'xiaohongshu_seeding', label: '种草文', hint: '推荐与安利' },
    { value: 'xiaohongshu_review', label: '测评文', hint: '对比与结论' },
    { value: 'xiaohongshu_story', label: '故事文', hint: '叙事与代入' },
    { value: 'wechat_marketing', label: '营销文', hint: '转化导向' },
    { value: 'wechat_tutorial', label: '教程文', hint: '步骤拆解' },
    { value: 'product_image_generation_pro', label: '产品视觉渲染', hint: '产品海报与视觉方案' },
];
const DEFAULT_SKILL_NAME = 'xiaohongshu_default';
const COLLAPSED_SKILL_COUNT = 6;
const OUTLINE_DRAFT_KEY_PREFIX = 'contentos:outline-wizard:draft:v1';
const SKILL_HINT_BY_VALUE = SKILL_OPTIONS.reduce((acc, item) => {
    acc[item.value] = item.hint || '';
    return acc;
}, {});

const normalizeDraftTopic = (value) => encodeURIComponent(String(value || '').trim().toLowerCase().slice(0, 160));
const clampTopic = (value) => String(value || '').slice(0, TOPIC_MAX_LENGTH);

const buildOutlineDraftKeys = ({ taskId, topic, skillName, pageCount }) => {
    const keys = [];
    const normalizedTaskId = String(taskId || '').trim();
    if (normalizedTaskId) {
        keys.push(`${OUTLINE_DRAFT_KEY_PREFIX}:task:${normalizedTaskId}`);
    }
    const topicKey = normalizeDraftTopic(topic);
    const skillKey = String(skillName || DEFAULT_SKILL_NAME).trim() || DEFAULT_SKILL_NAME;
    const countKey = Number(pageCount) || 0;
    keys.push(`${OUTLINE_DRAFT_KEY_PREFIX}:topic:${topicKey}:skill:${skillKey}:pages:${countKey}`);
    return [...new Set(keys)];
};

const saveOutlineDraftToLocal = ({ taskId, topic, skillName, pageCount, pages, activePageIndex }) => {
    if (typeof window === 'undefined') return;
    const safePages = Array.isArray(pages) ? pages : [];
    if (safePages.length === 0) return;

    const payload = {
        version: 1,
        taskId: String(taskId || '').trim(),
        topic: String(topic || ''),
        skillName: String(skillName || DEFAULT_SKILL_NAME),
        pageCount: Number(pageCount) || 0,
        pages: safePages,
        activePageIndex: Number(activePageIndex) || 0,
        updatedAt: Date.now(),
    };

    try {
        buildOutlineDraftKeys(payload).forEach((key) => {
            window.localStorage.setItem(key, JSON.stringify(payload));
        });
    } catch {
        // ignore storage write errors
    }
};

const loadOutlineDraftFromLocal = ({ taskId, topic, skillName, pageCount }) => {
    if (typeof window === 'undefined') return null;
    let latest = null;

    try {
        buildOutlineDraftKeys({ taskId, topic, skillName, pageCount }).forEach((key) => {
            const raw = window.localStorage.getItem(key);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!parsed || !Array.isArray(parsed.pages) || parsed.pages.length === 0) return;
            const updatedAt = Number(parsed.updatedAt || 0);
            if (!latest || updatedAt > Number(latest.updatedAt || 0)) {
                latest = parsed;
            }
        });
    } catch {
        return null;
    }

    return latest;
};

const clearOutlineDraftFromLocal = ({ taskId, topic, skillName, pageCount }) => {
    if (typeof window === 'undefined') return;
    try {
        buildOutlineDraftKeys({ taskId, topic, skillName, pageCount }).forEach((key) => {
            window.localStorage.removeItem(key);
        });
    } catch {
        // ignore storage remove errors
    }
};

const formatSkillLabel = (skillName, description = '') => {
    const matched = SKILL_OPTIONS.find((item) => item.value === skillName);
    if (matched) return matched.label;

    const desc = String(description || '').trim();
    if (desc && desc.length <= 12) return desc;

    if (skillName.startsWith('asset_')) return `模板 ${skillName.replace('asset_', '#')}`;
    if (skillName.startsWith('user_')) return `我的模板 ${skillName.replace('user_', '#')}`;
    if (skillName.startsWith('public_')) return `公开模板 ${skillName.replace('public_', '#')}`;
    return skillName.replace(/^(xiaohongshu_|wechat_)/, '').replace(/_/g, ' ').trim() || skillName;
};

const extractSkillOptionsFromPayload = (payload) => {
    const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.results) ? payload.results : []);
    return list
        .map((item) => {
            const value = String(item?.name || '').trim();
            if (!value) return null;
            const description = String(item?.description || '').trim();
            return {
                value,
                label: formatSkillLabel(value, description),
                hint: SKILL_HINT_BY_VALUE[value] || description || '自定义文案风格',
            };
        })
        .filter(Boolean);
};

const mergeSkillOptions = (dynamicOptions) => {
    const merged = new Map(SKILL_OPTIONS.map((item) => [item.value, item]));
    (dynamicOptions || []).forEach((item) => {
        if (!item?.value) return;
        merged.set(item.value, {
            ...merged.get(item.value),
            ...item,
        });
    });
    return Array.from(merged.values());
};

/* ── 轻量 Markdown → HTML（大纲场景：标题/加粗/列表/分隔线） ── */
const simpleMarkdown = (text) => {
    if (!text) return '';
    return text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') // escape HTML
        .replace(/^### (.+)$/gm, '<h3 class="smd-h3">$1</h3>')
        .replace(/^## (.+)$/gm, '<h2 class="smd-h2">$1</h2>')
        .replace(/^# (.+)$/gm, '<h1 class="smd-h1">$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/^[-*] (.+)$/gm, '<li class="smd-li">$1</li>')
        .replace(/^\d+\. (.+)$/gm, '<li class="smd-li smd-ol">$1</li>')
        .replace(/^---$/gm, '<hr class="smd-hr"/>')
        .replace(/\n/g, '<br/>');
};

const detectPageTypeByContent = (content, index, total) => {
    const firstLine = String(content || '').split('\n')[0]?.trim() || '';
    if (index === 0) return '封面';
    if (index === total - 1 && total > 1) return '总结';
    if (firstLine.startsWith('[封面]')) return '封面';
    if (firstLine.startsWith('[总结]')) return '总结';
    if (firstLine.startsWith('[内容]')) return '内容';
    return '内容';
};

const parseStreamingOutlinePages = (outlineText = '') => {
    const normalized = String(outlineText || '').trim();
    if (!normalized) return [];

    // 流式阶段优先按 <page> 开标签切块，兼容“后续页尚未闭合”的增量输出
    if (/<page>/i.test(normalized)) {
        const parts = normalized
            .split(/<page>/i)
            .slice(1)
            .map((seg) => seg.replace(/<\/page>/gi, '').trim())
            .filter(Boolean)
            .slice(0, 18);

        if (parts.length > 0) {
            const total = parts.length;
            return parts.map((content, idx) => ({
                order: idx + 1,
                type: detectPageTypeByContent(content, idx, total),
                content,
            }));
        }
    }

    return parseOutlinePages(normalized);
};

const normalizeSkillName = (value, allowedSkillNames) => {
    const candidate = String(value || '').trim();
    if (!candidate) return DEFAULT_SKILL_NAME;
    // Keep known skill prefixes to avoid downgrade before dynamic skills are loaded.
    if (/^(product_image_|xiaohongshu_|wechat_|asset_|user_|public_)/.test(candidate)) {
        return candidate;
    }
    if (allowedSkillNames instanceof Set) {
        return allowedSkillNames.has(candidate) ? candidate : DEFAULT_SKILL_NAME;
    }
    return SKILL_OPTIONS.some((item) => item.value === candidate) ? candidate : DEFAULT_SKILL_NAME;
};

const StepIndicator = ({ step }) => (
    <div className="flex items-center gap-3">
        {/* Step 1 */}
        <div className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${step >= 1 ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-gray-200 text-gray-500'
                }`}>1</div>
            <span className={`text-sm font-medium ${step >= 1 ? 'text-gray-900' : 'text-gray-400'}`}>配置</span>
        </div>
        {/* Line */}
        <div className={`h-0.5 w-8 rounded-full transition-all ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} />
        {/* Step 2 */}
        <div className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${step >= 2 ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-gray-200 text-gray-500'
                }`}>2</div>
            <span className={`text-sm font-medium ${step >= 2 ? 'text-gray-900' : 'text-gray-400'}`}>大纲编辑</span>
        </div>
    </div>
);

const OutlineEditorSkeleton = ({ pageCount = 5 }) => {
    const skeletonPages = Math.max(3, Math.min(pageCount || 5, 7));
    return (
        <div>
            <div className="mb-3 flex items-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="font-medium">正在生成大纲，请稍候…</span>
            </div>

            <div className="flex gap-3" style={{ height: '360px' }}>
                <div className="flex shrink-0 flex-col gap-1.5" style={{ width: '72px' }}>
                    {Array.from({ length: skeletonPages }).map((_, idx) => (
                        <div
                            key={`sk-page-${idx}`}
                            className="h-[38px] rounded-xl bg-gray-200/80 animate-pulse"
                        />
                    ))}
                </div>

                <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between border-b border-gray-100 bg-white px-4 py-2.5">
                        <div className="h-5 w-14 rounded-md bg-gray-200 animate-pulse" />
                        <div className="h-4 w-16 rounded bg-gray-200 animate-pulse" />
                    </div>
                    <div className="flex-1 p-3">
                        <div className="h-full w-full rounded-xl bg-white px-4 py-4">
                            <div className="space-y-3">
                                <div className="h-4 w-[90%] rounded bg-gray-200 animate-pulse" />
                                <div className="h-4 w-[76%] rounded bg-gray-200 animate-pulse" />
                                <div className="h-4 w-[88%] rounded bg-gray-200 animate-pulse" />
                                <div className="h-4 w-[68%] rounded bg-gray-200 animate-pulse" />
                                <div className="h-4 w-[82%] rounded bg-gray-200 animate-pulse" />
                                <div className="h-4 w-[58%] rounded bg-gray-200 animate-pulse" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContentWizardModal = ({ open, onClose, hotspot, token, onComplete }) => {
    const [step, setStep] = useState(1);
    const [topic, setTopic] = useState(hotspot?.topic || hotspot?.title || '');
    const [skillName, setSkillName] = useState(hotspot?.style || DEFAULT_SKILL_NAME);
    const hotspotContext = useMemo(() => {
        if (!hotspot?.hotspot_context) return null;
        const ctx = hotspot.hotspot_context;
        if (!ctx.summary && !ctx.desc && !ctx.link) return null;
        return ctx;
    }, [hotspot]);
    const [skillOptions, setSkillOptions] = useState(SKILL_OPTIONS);
    const [showAllSkills, setShowAllSkills] = useState(false);
    const [pageCount, setPageCount] = useState(5);
    const [pages, setPages] = useState([]);
    const [taskId, setTaskId] = useState('');
    const [topicLimitExceeded, setTopicLimitExceeded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [activePageIndex, setActivePageIndex] = useState(0);
    const [outlinePreview, setOutlinePreview] = useState(true);
    const [streamingOutline, setStreamingOutline] = useState(false);
    const [autoFollowStream, setAutoFollowStream] = useState(true);
    const [streamFrontierIndex, setStreamFrontierIndex] = useState(0);
    const allowedSkillNames = useMemo(
        () => new Set(skillOptions.map((item) => item.value)),
        [skillOptions]
    );
    const normalizedSkillName = useMemo(
        () => normalizeSkillName(skillName, allowedSkillNames),
        [skillName, allowedSkillNames]
    );
    const visibleSkillOptions = useMemo(() => {
        if (showAllSkills || skillOptions.length <= COLLAPSED_SKILL_COUNT) {
            return skillOptions;
        }

        const collapsed = skillOptions.slice(0, COLLAPSED_SKILL_COUNT);
        if (collapsed.some((item) => item.value === normalizedSkillName)) {
            return collapsed;
        }

        const selected = skillOptions.find((item) => item.value === normalizedSkillName);
        if (!selected) return collapsed;
        return [selected, ...collapsed.slice(0, COLLAPSED_SKILL_COUNT - 1)];
    }, [skillOptions, showAllSkills, normalizedSkillName]);
    const autoGenerateRef = useRef(false);
    const abortRef = useRef(null);
    const streamTextRef = useRef('');
    const streamRenderedLengthRef = useRef(0);
    const streamFrameRef = useRef(0);
    const outlinePreviewRef = useRef(null);
    const autoFollowRef = useRef(true);
    const draftPersistTimerRef = useRef(null);

    const handleTopicChange = useCallback((value) => {
        const nextValue = String(value || '');
        setTopicLimitExceeded(nextValue.length > TOPIC_MAX_LENGTH);
        setTopic(clampTopic(nextValue));
    }, []);

    // Reset state when hotspot changes
    React.useEffect(() => {
        if (open && hotspot) {
            if (streamFrameRef.current) {
                window.cancelAnimationFrame(streamFrameRef.current);
                streamFrameRef.current = 0;
            }
            const t = hotspot?.topic || hotspot?.title || '';
            const initialSkill = hotspot?.style || DEFAULT_SKILL_NAME;
            const pc = hotspot?.pageCount || 5;
            const cachedDraft = loadOutlineDraftFromLocal({
                taskId: hotspot?.task_id || '',
                topic: t,
                skillName: initialSkill,
                pageCount: pc,
            });

            const nextTopic = cachedDraft?.topic || t;
            setTopic(clampTopic(nextTopic));
            setTopicLimitExceeded(String(nextTopic || '').length > TOPIC_MAX_LENGTH);
            setSkillName(cachedDraft?.skillName || initialSkill);
            setShowAllSkills(false);
            setPageCount(Number(cachedDraft?.pageCount) || pc);
            setPages([]);
            setTaskId(cachedDraft?.taskId || '');
            setError('');
            setSubmitting(false);
            setActivePageIndex(0);
            setStreamingOutline(false);
            setAutoFollowStream(true);
            autoFollowRef.current = true;
            setStreamFrontierIndex(0);
            streamTextRef.current = '';
            streamRenderedLengthRef.current = 0;

            if (cachedDraft?.pages?.length > 0) {
                const normalizeDraftPages = (outlinePages, expectedPageCount) => {
                    let adjusted = (outlinePages || []).slice(0, expectedPageCount);
                    while (adjusted.length < expectedPageCount) {
                        const idx = adjusted.length + 1;
                        const type = idx === 1 ? '封面' : idx === expectedPageCount ? '总结' : '内容';
                        adjusted.push({ type, content: '', order: idx });
                    }
                    return adjusted.map((page, idx) => {
                        let type = '内容';
                        if (idx === 0) type = '封面';
                        else if (idx === expectedPageCount - 1 && expectedPageCount > 1) type = '总结';
                        return {
                            ...page,
                            order: idx + 1,
                            type,
                        };
                    });
                };
                const restoredPageCount = Number(cachedDraft?.pageCount) || pc;
                const restoredPages = normalizeDraftPages(cachedDraft.pages, restoredPageCount);
                const restoredActiveIndex = Math.max(0, Math.min(Number(cachedDraft?.activePageIndex) || 0, restoredPages.length - 1));
                const lastFilledIndex = restoredPages.reduce((acc, page, idx) => (
                    String(page?.content || '').trim() ? idx : acc
                ), 0);
                setTaskId(cachedDraft?.taskId || hotspot?.task_id || '');
                setPages(restoredPages);
                setActivePageIndex(restoredActiveIndex);
                setStep(2);
                setLoading(false);
                setStreamingOutline(false);
                setStreamFrontierIndex(lastFilledIndex);
                return;
            }

            // 从历史页打开 draft 任务（已有 outline + task_id）
            if (hotspot?.outline && hotspot?.task_id) {
                setTaskId(hotspot.task_id);
                // 解析已有大纲为页面
                const parsed = parseOutlinePages(hotspot.outline);
                setPages(parsed.length > 0 ? parsed : [{ type: '封面', content: '', order: 1 }]);
                setStep(2);
                setLoading(false);
                setStreamingOutline(false);
                setStreamFrontierIndex(Math.max(0, parsed.length - 1));
            }
            // 从创作页进入时（已带 pageCount），直接进入步骤2并自动生成
            else if (hotspot?.pageCount && t) {
                setStep(2);
                setLoading(true);
                setStreamingOutline(false);
                autoGenerateRef.current = true;
            } else {
                setStep(1);
                setLoading(false);
                setStreamingOutline(false);
            }
        }
    }, [open, hotspot]);

    React.useEffect(() => {
        if (!open) return;

        let cancelled = false;
        const loadSkillOptions = async () => {
            try {
                const payload = await getSkills(token);
                if (cancelled) return;

                const dynamicOptions = extractSkillOptionsFromPayload(payload);
                if (dynamicOptions.length > 0) {
                    setSkillOptions(mergeSkillOptions(dynamicOptions));
                } else {
                    setSkillOptions(SKILL_OPTIONS);
                }
            } catch {
                if (!cancelled) {
                    setSkillOptions(SKILL_OPTIONS);
                }
            }
        };

        loadSkillOptions();
        return () => {
            cancelled = true;
        };
    }, [open, token]);

    React.useEffect(() => {
        autoFollowRef.current = autoFollowStream;
    }, [autoFollowStream]);

    const normalizeOutlinePages = useCallback((outlinePages, expectedPageCount) => {
        let adjusted = (outlinePages || []).slice(0, expectedPageCount);
        while (adjusted.length < expectedPageCount) {
            const idx = adjusted.length + 1;
            const type = idx === 1 ? '封面' : idx === expectedPageCount ? '总结' : '内容';
            adjusted.push({ type, content: '', order: idx });
        }
        return adjusted.map((page, idx) => {
            let type = '内容';
            if (idx === 0) type = '封面';
            else if (idx === expectedPageCount - 1 && expectedPageCount > 1) type = '总结';
            return {
                ...page,
                order: idx + 1,
                type,
            };
        });
    }, []);

    const flushStreamPreview = useCallback((effectivePageCount) => {
        streamFrameRef.current = 0;
        const currentText = streamTextRef.current;
        const hasPageMarker = currentText.includes('</page>') || currentText.includes('<page>');
        const deltaLength = currentText.length - streamRenderedLengthRef.current;
        if (!hasPageMarker && deltaLength < 160) return;

        const parsed = parseStreamingOutlinePages(currentText);
        if (parsed.length > 0) {
            streamRenderedLengthRef.current = currentText.length;
            setPages(normalizeOutlinePages(parsed, effectivePageCount));
            const maxIndex = Math.max(0, Math.min(parsed.length - 1, effectivePageCount - 1));
            setStreamFrontierIndex((prev) => Math.max(prev, maxIndex));
            if (autoFollowRef.current) {
                setActivePageIndex(maxIndex);
            } else {
                setActivePageIndex((prev) => Math.max(0, Math.min(prev, maxIndex)));
            }
            setLoading(false);
        }
    }, [normalizeOutlinePages]);

    const startOutlineGeneration = useCallback(async (effectiveTopic, effectiveSkillName, effectivePageCount) => {
        if (!effectiveTopic?.trim() || loading) return;
        if (String(effectiveTopic || '').length > TOPIC_MAX_LENGTH) {
            setError(`主题最多支持 ${TOPIC_MAX_LENGTH} 字，请精简后再生成`);
            return;
        }

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setStep(2);
        setLoading(true);
        setStreamingOutline(true);
        setError('');
        setTaskId('');
        setPages(normalizeOutlinePages([], effectivePageCount));
        setActivePageIndex(0);
        setOutlinePreview(true);
        setAutoFollowStream(true);
        autoFollowRef.current = true;
        setStreamFrontierIndex(0);
        streamTextRef.current = '';
        streamRenderedLengthRef.current = 0;

        try {
            const result = await generateOutlineStream(
                effectiveTopic,
                effectiveSkillName,
                token,
                effectivePageCount,
                {
                    signal: controller.signal,
                    context: hotspotContext,
                    onDelta: (chunk) => {
                        streamTextRef.current += chunk;
                        if (streamFrameRef.current) return;
                        streamFrameRef.current = window.requestAnimationFrame(() => {
                            flushStreamPreview(effectivePageCount);
                        });
                    },
                }
            );

            if (controller.signal.aborted) return;

            const parsedFromResult = result?.outline?.pages || [];
            const fallbackParsed = parseOutlinePages(result?.outline_text || streamTextRef.current || '');
            const finalPages = parsedFromResult.length > 0 ? parsedFromResult : fallbackParsed;
            setPages(normalizeOutlinePages(finalPages, effectivePageCount));
            const maxIndex = Math.max(0, Math.min(finalPages.length - 1, effectivePageCount - 1));
            setStreamFrontierIndex(maxIndex);
            if (autoFollowRef.current) {
                setActivePageIndex(maxIndex);
            } else {
                setActivePageIndex((prev) => Math.max(0, Math.min(prev, maxIndex)));
            }
            setTaskId(result?.task_id || '');
        } catch (err) {
            if (err.name === 'AbortError') return;

            // 兜底：流式失败后尝试走一次性接口
            try {
                const fallback = await generateOutline(
                    effectiveTopic,
                    effectiveSkillName,
                    token,
                    effectivePageCount,
                    { signal: controller.signal, context: hotspotContext }
                );
                if (!controller.signal.aborted) {
                    const outlinePages = fallback?.outline?.pages || [];
                    setPages(normalizeOutlinePages(outlinePages, effectivePageCount));
                    const maxIndex = Math.max(0, Math.min(outlinePages.length - 1, effectivePageCount - 1));
                    setStreamFrontierIndex(maxIndex);
                    if (autoFollowRef.current) {
                        setActivePageIndex(maxIndex);
                    } else {
                        setActivePageIndex((prev) => Math.max(0, Math.min(prev, maxIndex)));
                    }
                    setTaskId(fallback?.task_id || '');
                }
            } catch (fallbackErr) {
                if (fallbackErr.name !== 'AbortError') {
                    setError(fallbackErr.message || err.message || '大纲生成失败，请稍后重试');
                }
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
                setStreamingOutline(false);
            }
        }
    }, [loading, token, normalizeOutlinePages, flushStreamPreview]);

    const handleGenerateOutline = useCallback(async () => {
        await startOutlineGeneration(topic, normalizedSkillName, pageCount);
    }, [startOutlineGeneration, topic, normalizedSkillName, pageCount]);

    // 自动触发大纲生成（从创作页进入时，跳过步骤1）
    React.useEffect(() => {
        if (autoGenerateRef.current && open && (hotspot?.topic || hotspot?.title || topic)) {
            autoGenerateRef.current = false;
            const effectiveTopic = hotspot?.topic || hotspot?.title || topic;
            const effectiveSkillName = normalizeSkillName(hotspot?.style || skillName, allowedSkillNames);
            const effectivePageCount = hotspot?.pageCount || pageCount;
            startOutlineGeneration(effectiveTopic, effectiveSkillName, effectivePageCount);
        }
    }, [open, hotspot, topic, skillName, pageCount, startOutlineGeneration, allowedSkillNames]);

    React.useEffect(() => {
        if (!streamingOutline || !outlinePreview || !autoFollowStream) return;
        if (activePageIndex !== streamFrontierIndex) return;
        const el = outlinePreviewRef.current;
        if (!el) return;
        const timer = window.setTimeout(() => {
            el.scrollTop = el.scrollHeight;
        }, 0);
        return () => window.clearTimeout(timer);
    }, [pages, activePageIndex, streamFrontierIndex, streamingOutline, outlinePreview, autoFollowStream]);

    React.useEffect(() => () => {
        if (streamFrameRef.current) {
            window.cancelAnimationFrame(streamFrameRef.current);
            streamFrameRef.current = 0;
        }
    }, []);

    React.useEffect(() => {
        if (step !== 2 || pages.length > 0) return;
        setPages(normalizeOutlinePages([], pageCount));
        setActivePageIndex(0);
    }, [step, pages.length, pageCount, normalizeOutlinePages]);

    const handlePageContentChange = useCallback((index, newContent) => {
        setPages(prev => prev.map((p, i) => i === index ? { ...p, content: newContent } : p));
    }, []);

    const persistOutlineDraftNow = useCallback((options = {}) => {
        const snapshot = {
            taskId: options.taskId ?? taskId,
            topic: options.topic ?? topic,
            skillName: options.skillName ?? normalizedSkillName,
            pageCount: options.pageCount ?? pageCount,
            pages: options.pages ?? pages,
            activePageIndex: options.activePageIndex ?? activePageIndex,
        };
        saveOutlineDraftToLocal(snapshot);
    }, [taskId, topic, normalizedSkillName, pageCount, pages, activePageIndex]);

    React.useEffect(() => {
        if (!open || step !== 2 || pages.length === 0) return;
        if (loading || streamingOutline) return;

        if (draftPersistTimerRef.current) {
            window.clearTimeout(draftPersistTimerRef.current);
        }
        draftPersistTimerRef.current = window.setTimeout(() => {
            persistOutlineDraftNow();
        }, 180);

        return () => {
            if (draftPersistTimerRef.current) {
                window.clearTimeout(draftPersistTimerRef.current);
                draftPersistTimerRef.current = null;
            }
        };
    }, [open, step, pages, loading, streamingOutline, persistOutlineDraftNow]);

    const handleRequestClose = useCallback(() => {
        abortRef.current?.abort();

        const currentPages = Array.isArray(pages) ? pages : [];
        if (step === 2 && currentPages.length > 0) {
            const currentSkillName = normalizedSkillName;
            const currentTaskId = String(taskId || '').trim();
            const currentTopic = topic;
            const currentPageCount = pageCount;
            const currentActivePageIndex = activePageIndex;
            persistOutlineDraftNow({
                taskId: currentTaskId,
                topic: currentTopic,
                skillName: currentSkillName,
                pageCount: currentPageCount,
                pages: currentPages,
                activePageIndex: currentActivePageIndex,
            });

            if (currentTaskId && !loading && !streamingOutline && !submitting) {
                const outlineText = currentPages.map((page) => `<page>${page.content || ''}</page>`).join('\n');
                updateTaskOutline(currentTaskId, outlineText, token).catch(() => {
                    // keep local draft; backend update is best-effort on close
                });
            }
        }

        onClose?.();
    }, [
        step,
        pages,
        normalizedSkillName,
        taskId,
        topic,
        pageCount,
        activePageIndex,
        persistOutlineDraftNow,
        loading,
        streamingOutline,
        submitting,
        token,
        onClose,
    ]);

    const handleSubmit = useCallback(async () => {
        if (submitting || loading || streamingOutline) return;
        if (String(topic || '').length > TOPIC_MAX_LENGTH) {
            setError(`主题最多支持 ${TOPIC_MAX_LENGTH} 字，请精简后再提交`);
            return;
        }
        setSubmitting(true);
        setError('');

        // 拼接编辑后的大纲文本
        const outlineText = pages.map(p => `<page>${p.content}</page>`).join('\n');
        const pagePayload = pages.length > 0 ? pages : [{ type: '封面', content: topic || '', order: 1 }];
        if (pagePayload.length > MAX_IMAGE_COUNT) {
            setError(`当前大纲共 ${pagePayload.length} 页，暂最多支持 ${MAX_IMAGE_COUNT} 页配图生成，请先精简页数`);
            setSubmitting(false);
            return;
        }

        try {
            let effectiveTaskId = String(taskId || '').trim();
            if (!effectiveTaskId) {
                const created = await createContentTask(
                    String(topic || '').trim() || '未命名主题',
                    normalizedSkillName,
                    token
                );
                effectiveTaskId = String(created?.task_id || '').trim();
                if (!effectiveTaskId) {
                    throw new Error('任务创建失败，请稍后重试');
                }
                setTaskId(effectiveTaskId);
            }

            await submitTaskGeneration(
                effectiveTaskId,
                outlineText,
                pagePayload,
                token,
                {
                    referenceImageUrl: hotspot?.referenceImageUrl || '',
                }
            );
            clearOutlineDraftFromLocal({
                taskId: effectiveTaskId,
                topic,
                skillName: normalizedSkillName,
                pageCount,
            });

            // 立即跳转历史页
            onComplete?.();
        } catch (err) {
            setError(err?.message || '提交失败，请稍后重试');
            setSubmitting(false);
        }
    }, [pages, taskId, topic, token, submitting, loading, streamingOutline, onComplete, hotspot, normalizedSkillName, pageCount]);

    if (!open) return null;

    const estimatedCost = 1 + (SUBMIT_IMAGE_COST * pageCount);
    const hasOutlineContent = pages.some((page) => Boolean(String(page?.content || '').trim()));
    const showOutlineSkeleton = step === 2 && (loading || streamingOutline) && !hasOutlineContent;
    const canJumpToFrontier = streamingOutline && streamFrontierIndex >= 0;
    const showFollowLatestAction = canJumpToFrontier && (!autoFollowStream || activePageIndex !== streamFrontierIndex);

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={handleRequestClose} />

            {/* Modal */}
            <div className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl animate-fade-in"
                style={{ maxHeight: '88vh' }}
            >
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white px-6 py-4">
                    <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-500 shadow-lg shadow-blue-200">
                            <Sparkles className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">内容创作向导</h3>
                            <StepIndicator step={step} />
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleRequestClose}
                        className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Error — 简洁友好提示 */}
                    {error && (() => {
                        const raw = String(error || '');
                        const refundMatch = raw.match(/[，,]\s*已自动退还\s*(\d+)\s*积分/);
                        const refundPoints = refundMatch ? refundMatch[1] : null;
                        const coreMsg = refundMatch ? raw.replace(refundMatch[0], '').trim() : raw.trim();

                        // 积分不足 / 页数超限 → 保留原文；其余后端异常 → 统一简洁提示
                        let displayMsg = '服务暂不可用，请稍后重试';
                        if (/积分不足/.test(coreMsg) || /需要.*积分/.test(coreMsg)) {
                            const m = coreMsg.match(/需要\s*(\d+)\s*积分.*余额\s*(\d+)/);
                            displayMsg = m ? `积分不足，需要 ${m[1]} 积分，当前余额 ${m[2]}` : coreMsg;
                        } else if (/最多支持/.test(coreMsg) || /页.*超/.test(coreMsg)) {
                            displayMsg = coreMsg;
                        }

                        return (
                            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-3.5 shadow-sm transition-all">
                                <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
                                <span className="flex-1 text-sm font-medium text-gray-700">{displayMsg}</span>
                                {refundPoints && (
                                    <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium text-emerald-600">
                                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" /></svg>
                                        已退还 {refundPoints} 积分
                                    </span>
                                )}
                                <button type="button" onClick={() => setError('')} className="shrink-0 rounded-lg p-1 text-gray-300 transition-colors hover:bg-white/60 hover:text-gray-500">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        );
                    })()}

                    {step === 1 && (
                        <div className="space-y-6">
                            {/* Topic */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">主题</label>
                                <textarea
                                    value={topic}
                                    onChange={e => handleTopicChange(e.target.value)}
                                    rows={3}
                                    maxLength={TOPIC_MAX_LENGTH}
                                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100"
                                    placeholder="输入或编辑创作主题…"
                                />
                                <div className="mt-2 flex items-center justify-between gap-3">
                                    <span className={`text-xs ${topicLimitExceeded ? 'text-red-500' : 'text-gray-400'}`}>
                                        {topicLimitExceeded ? `最多支持 ${TOPIC_MAX_LENGTH} 字，超出部分已自动截断` : `主题最多 ${TOPIC_MAX_LENGTH} 字`}
                                    </span>
                                    <span className={`text-xs tabular-nums ${topic.length >= TOPIC_MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                                        {topic.length}/{TOPIC_MAX_LENGTH}
                                    </span>
                                </div>
                            </div>

                            {/* Page Count */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">页数</label>
                                <p className="mb-3 text-xs text-gray-400">选择小红书图片页数，每页将生成一段大纲和对应图片</p>
                                <div className="flex gap-2">
                                    {PAGE_OPTIONS.map(n => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setPageCount(n)}
                                            className={`flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-bold transition-all ${pageCount === n
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">风格</label>
                                <p className="mb-3 text-xs text-gray-400">选择文案表达方式，直接卡片切换，避免下拉遮挡</p>
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                    {visibleSkillOptions.map((option) => {
                                        const isActive = normalizedSkillName === option.value;
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setSkillName(option.value)}
                                                aria-pressed={isActive}
                                                className={`group min-h-[52px] rounded-xl border px-3 py-2 text-left transition-all ${isActive
                                                    ? 'border-blue-500 bg-blue-50 shadow-sm shadow-blue-100'
                                                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40'
                                                    }`}
                                            >
                                                <div className={`text-sm font-semibold ${isActive ? 'text-blue-700' : 'text-gray-700'}`}>{option.label}</div>
                                                <div className={`mt-0.5 text-xs ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-blue-500'}`}>{option.hint}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                                {skillOptions.length > COLLAPSED_SKILL_COUNT && (
                                    <div className="mt-2 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setShowAllSkills((prev) => !prev)}
                                            className="rounded-lg px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
                                        >
                                            {showAllSkills ? '收起风格' : `展开全部 ${skillOptions.length} 个风格`}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Cost estimate */}
                            <div className="flex items-center gap-2 rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
                                <Zap className="h-4 w-4 text-amber-500" />
                                <span className="text-sm text-amber-700">
                                    预估消耗 <strong>{estimatedCost}</strong> 积分（大纲 1 + 图片 {6 * pageCount}）
                                </span>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            {showOutlineSkeleton ? (
                                <OutlineEditorSkeleton pageCount={pages.length || pageCount} />
                            ) : (
                                <>
                                    {/* 大纲编辑分栏 */}
                                    <div>
                                        <div className="mb-3 flex items-center justify-between gap-2 text-sm text-gray-500">
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-4 w-4" />
                                                <span>共 {pages.length} 页大纲，点击左侧页码切换编辑</span>
                                            </div>
                                            {showFollowLatestAction ? (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setAutoFollowStream(true);
                                                        autoFollowRef.current = true;
                                                        setOutlinePreview(true);
                                                        setActivePageIndex(streamFrontierIndex);
                                                    }}
                                                    className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-100"
                                                >
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    回到最新生成
                                                </button>
                                            ) : (
                                                streamingOutline && (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-500">
                                                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                                                        自动跟随中
                                                    </span>
                                                )
                                            )}
                                        </div>

                                        <div className="flex gap-3" style={{ height: '360px' }}>
                                            {/* 左侧页码导航 */}
                                            <div className="flex flex-col gap-1.5 shrink-0" style={{ width: '72px' }}>
                                                {pages.map((page, idx) => {
                                                    const isActive = idx === activePageIndex;
                                                    const hasContent = Boolean(page.content?.trim());
                                                    const isGenerated = idx <= streamFrontierIndex && hasContent;
                                                    const label = idx === 0 ? '封面' : `第${idx + 1}页`;
                                                    return (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            onClick={() => {
                                                                if (streamingOutline) {
                                                                    setAutoFollowStream(false);
                                                                    autoFollowRef.current = false;
                                                                }
                                                                setActivePageIndex(idx);
                                                            }}
                                                            className={`relative flex items-center justify-center rounded-xl px-2 py-2.5 text-xs font-bold transition-all cursor-pointer ${isActive
                                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105'
                                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                                                                }`}
                                                        >
                                                            {label}
                                                            {/* 已编辑指示点 */}
                                                            {!isActive && hasContent && (
                                                                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500" />
                                                            )}
                                                            {!isActive && streamingOutline && isGenerated && (
                                                                <span className="absolute -left-0.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-blue-400" />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {/* 右侧编辑区 */}
                                            <div className="flex-1 flex flex-col rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
                                                {/* 编辑区头部 */}
                                                <div className="flex items-center justify-between border-b border-gray-100 bg-white px-4 py-2.5">
                                                    <span className={`inline-flex items-center rounded-lg px-2.5 py-0.5 text-xs font-bold ${activePageIndex === 0
                                                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white'
                                                        : 'bg-gray-200 text-gray-600'
                                                        }`}>
                                                        {activePageIndex === 0 ? '封面' : `第${activePageIndex + 1}页`}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-400">
                                                            {activePageIndex + 1} / {pages.length} 页
                                                        </span>
                                                        {!outlinePreview && (
                                                            <span className="text-[10px] text-blue-500 font-medium">编辑中…</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* 内容区：默认预览，点击进入编辑，失焦回到预览 */}
                                                <div className="flex-1 min-h-0 p-3">
                                                    {outlinePreview ? (
                                                        <div
                                                            ref={outlinePreviewRef}
                                                            onClick={() => {
                                                                setAutoFollowStream(false);
                                                                autoFollowRef.current = false;
                                                                setOutlinePreview(false);
                                                            }}
                                                            className="h-full w-full rounded-xl bg-white px-4 py-3 text-sm text-gray-700 leading-relaxed overflow-y-auto smd-preview cursor-text ring-2 ring-transparent hover:ring-blue-100 transition-shadow"
                                                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(simpleMarkdown(pages[activePageIndex]?.content || '') || '<span class=\"text-gray-300\">点击编辑大纲内容…</span>') }}
                                                        />
                                                    ) : (
                                                        <textarea
                                                            autoFocus
                                                            value={pages[activePageIndex]?.content || ''}
                                                            onChange={e => handlePageContentChange(activePageIndex, e.target.value)}
                                                            onBlur={() => setOutlinePreview(true)}
                                                            className="h-full w-full resize-none rounded-xl border-0 bg-white px-4 py-3 text-sm text-gray-700 leading-relaxed outline-none transition-shadow placeholder:text-gray-300 ring-2 ring-blue-200"
                                                            placeholder={`${activePageIndex === 0 ? '封面' : `第${activePageIndex + 1}页`}的大纲内容…`}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex shrink-0 items-center justify-between border-t border-gray-100 bg-gray-50/50 px-6 py-4">
                    <div></div>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleRequestClose}
                            className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
                        >
                            取消
                        </button>

                        {step === 1 && (
                            <button
                                type="button"
                                onClick={handleGenerateOutline}
                                disabled={!topic.trim() || loading || topic.length > TOPIC_MAX_LENGTH}
                                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-all ${!topic.trim() || loading || topic.length > TOPIC_MAX_LENGTH
                                    ? 'cursor-not-allowed bg-gray-300 shadow-none'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-500 shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5'
                                    }`}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        生成大纲中…
                                    </>
                                ) : (
                                    <>
                                        生成大纲
                                        <ChevronRight className="h-4 w-4" />
                                    </>
                                )}
                            </button>
                        )}

                        {step === 2 && (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting || loading || streamingOutline}
                                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-all ${submitting || loading || streamingOutline
                                    ? 'cursor-not-allowed bg-gray-300 shadow-none'
                                    : 'bg-gradient-to-r from-emerald-600 to-emerald-500 shadow-emerald-200 hover:shadow-xl hover:-translate-y-0.5'
                                    }`}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        提交中…
                                    </>
                                ) : streamingOutline ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        生成大纲中…
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="h-4 w-4" />
                                        提交生成
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ContentWizardModal;
